import { Check, Copy } from "lucide-react";
import QRCode from "qrcode";
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Modal } from "./Modal";
import Toast from "./Toast";

interface QRCodeModalProps {
	isOpen: boolean;
	onClose: () => void;
	link: string;
	title?: string;
}

export default function QRCodeModal({
	isOpen,
	onClose,
	link,
	title = "QR-Code",
}: QRCodeModalProps) {
	const [qrDataUrl, setQrDataUrl] = useState<string>("");
	const [copied, setCopied] = useState(false);
	const [toastMessage, setToastMessage] = useState<string | null>(null);

	// QR-Code generieren
	useEffect(() => {
		if (isOpen && link) {
			QRCode.toDataURL(link, {
				errorCorrectionLevel: "H",
				margin: 1,
				width: 300,
				color: {
					dark: "#000000",
					light: "#FFFFFF",
				},
			})
				.then((url) => {
					setQrDataUrl(url);
				})
				.catch((err) => {
					console.error("QR-Code Generierungsfehler:", err);
				});
		}
	}, [isOpen, link]);

	// Link kopieren
	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text).then(
			() => {
				setCopied(true);
				setToastMessage("Link in die Zwischenablage kopiert!");
				setTimeout(() => setCopied(false), 2000);
			},
			() => {
				setToastMessage("Kopieren in die Zwischenablage fehlgeschlagen.");
			},
		);
	};

	// Toast ausblenden nach Zeit
	useEffect(() => {
		if (toastMessage) {
			const timer = setTimeout(() => setToastMessage(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [toastMessage]);

	return (
		<>
			<Toast
				message={toastMessage || ""}
				onClose={() => setToastMessage(null)}
				type={toastMessage?.includes("fehlgeschlagen") ? "error" : "success"}
				visible={!!toastMessage}
			/>

			<Modal
				closeOnOutsideClick
				isOpen={isOpen}
				onClose={onClose}
				title={title}
			>
				<div className="flex flex-col items-center justify-center gap-6 p-6">
					{qrDataUrl ? (
						<div className="rounded-xl bg-white p-4 shadow-inner">
							<img alt="QR-Code" className="h-64 w-64" src={qrDataUrl} />
						</div>
					) : (
						<div className="flex h-64 w-64 items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800">
							<div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
						</div>
					)}
					<div className="max-w-sm break-all text-center text-gray-600 text-sm dark:text-gray-300">
						{link}
					</div>
					<Button
						className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
						onClick={() => copyToClipboard(link)}
						type="button"
					>
						{copied ? (
							<Check className="h-4 w-4" />
						) : (
							<Copy className="h-4 w-4" />
						)}
						Link kopieren
					</Button>
				</div>
			</Modal>
		</>
	);
}
